import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DEAL_CARD_SURFACE_CONFIG } from './config';
import { useMembershipAccess } from '../membership';
import {
  createArticleModel,
  extractIndustries,
  formatCurrencyCompact,
  formatDateForSurface,
  getDealCardTitle,
  getParticipationType,
  hasRecipientCompanyData,
  mapInvestorGroups,
} from './helpers';

function resolveSurfaceConfig(surfaceKey, membershipAccess) {
  const surface = DEAL_CARD_SURFACE_CONFIG[surfaceKey];
  if (!surface) {
    throw new Error(`Unknown deal-card surface: ${surfaceKey}`);
  }

  const recipientCompanyAccessMode =
    surface.recipientCompany?.accessMode === 'membershipDefault'
      ? membershipAccess.dealCard.recipientCompanyAccessMode
      : surface.recipientCompany?.accessMode || 'professionalOnly';

  return {
    ...surface,
    membershipType: membershipAccess.membershipType,
    membershipConfig: membershipAccess.dealCard,
    recipientCompany: {
      visible: surface.recipientCompany?.visible !== false,
      accessMode: recipientCompanyAccessMode,
    },
  };
}

export function useDealCardCollection({
  deals = [],
  surfaceKey,
  buildInvestorNode,
}) {
  const { t } = useTranslation();
  const membership = useMembershipAccess();

  const surfaceConfig = useMemo(
    () => resolveSurfaceConfig(surfaceKey, membership),
    [membership.dealCard, membership.membershipType, surfaceKey]
  );

  const items = useMemo(
    () =>
      (Array.isArray(deals) ? deals : []).map((deal, index) => {
        const article = createArticleModel(
          deal.press_release_content_original_format,
          surfaceConfig.article
        );
        const investorGroups = mapInvestorGroups({
          deal,
          investorConfig: surfaceConfig.investors,
          buildInvestorNode,
          t,
        });
        const title = getDealCardTitle(deal, surfaceConfig);

        return {
          id: deal._id || deal.id || String(index),
          rawDeal: deal,
          title: title.title,
          titleText: title.titleText,
          titleUrl: title.titleUrl,
          announcementDate: formatDateForSurface(
            deal.announcement_date_dt,
            surfaceConfig.dateLocale
          ),
          amount: surfaceConfig.showAmount
            ? formatCurrencyCompact(deal.investment_currency, deal.am_usd) || undefined
            : undefined,
          round: surfaceConfig.showRound ? deal.investment_round || undefined : undefined,
          industries: surfaceConfig.showIndustries
            ? extractIndustries(deal.industry_info)
            : [],
          summary: article.summary,
          detailParagraphs: article.detailParagraphs,
          hasArticle: article.hasArticle,
          recipientCompany: deal.recipient_company,
          recipientCompanyUrl: deal.recipient_company_url,
          hasRecipientCompanyInfo:
            surfaceConfig.recipientCompany.visible && hasRecipientCompanyData(deal),
          recipientAccessMode: surfaceConfig.recipientCompany.accessMode,
          canViewContent: deal.can_view_content !== false,
          leadInvestors: investorGroups.leadInvestors,
          otherInvestors: investorGroups.otherInvestors,
          investorsTotal: investorGroups.investorsTotal,
          participationType: getParticipationType(deal),
          redacted: Boolean(deal.redacted),
        };
      }),
    [buildInvestorNode, deals, surfaceConfig, t]
  );

  return {
    items,
    surfaceConfig,
    membershipType: membership.membershipType,
  };
}
